import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./Extract.module.sass";
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import Checkbox from "../../components/Checkbox";
import { WithContext as ReactTags } from "react-tag-input";
import { getCollections, getCollectionFields, exportCollectionData, exportMultiCollectionData } from "../../services/export.service";
import { toast } from "react-toastify";

const KeyCodes = {
    comma: 188,
    enter: 13,
};
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const Extract = ({ className }) => {
    // États pour les collections et champs
    const [collections, setCollections] = useState([]);
    const [selectedCollection, setSelectedCollection] = useState("");
    const [collectionFields, setCollectionFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingFields, setLoadingFields] = useState(false);
    const [exporting, setExporting] = useState(false);

    // États pour les tags globaux (format "collection - champ")
    const [selectedFieldTags, setSelectedFieldTags] = useState([]); // [{id: "students-name", text: "students - name", collection: "students", field: "name"}]
    const [allCollectionFields, setAllCollectionFields] = useState(new Map()); // Cache des champs par collection

    // Charger les collections au montage du composant
    useEffect(() => {
        loadCollections();
    }, []);

    // Charger les champs quand une collection est sélectionnée
    useEffect(() => {
        if (selectedCollection && selectedCollection !== "Sélectionner une collection") {
            loadCollectionFields(selectedCollection);
        } else {
            setCollectionFields([]);
        }
    }, [selectedCollection]);

    const loadCollections = async () => {
        setLoading(true);
        try {
            const collectionsData = await getCollections();
            const collectionOptions = ["Sélectionner une collection", ...collectionsData.map(col => col.name)];
            setCollections(collectionOptions);
            setSelectedCollection(collectionOptions[0]);
        } catch (error) {
            console.error("Erreur lors du chargement des collections:", error);
            toast.error("Erreur lors du chargement des collections");
        } finally {
            setLoading(false);
        }
    };

    const loadCollectionFields = async (collectionName) => {
        setLoadingFields(true);
        try {
            const fieldsData = await getCollectionFields(collectionName);
            setCollectionFields(fieldsData);
            
            // Mettre en cache les champs de cette collection
            const newCache = new Map(allCollectionFields);
            newCache.set(collectionName, fieldsData);
            setAllCollectionFields(newCache);
        } catch (error) {
            console.error('Erreur lors du chargement des champs:', error);
            toast.error('Impossible de récupérer les champs de la collection');
        } finally {
            setLoadingFields(false);
        }
    };

    // Gérer la sélection/désélection d'un champ via checkbox
    const handleFieldChange = (fieldName) => {
        if (!selectedCollection || selectedCollection === "Sélectionner une collection") {
            return;
        }

        const tagId = `${selectedCollection}-${fieldName}`;
        const tagText = `${selectedCollection} - ${fieldName}`;
        
        const existingTagIndex = selectedFieldTags.findIndex(tag => tag.id === tagId);
        
        if (existingTagIndex >= 0) {
            // Retirer le tag
            const newTags = selectedFieldTags.filter((_, index) => index !== existingTagIndex);
            setSelectedFieldTags(newTags);
        } else {
            // Ajouter le tag
            const newTag = {
                id: tagId,
                text: tagText,
                collection: selectedCollection,
                field: fieldName
            };
            setSelectedFieldTags([...selectedFieldTags, newTag]);
        }
    };

    // Vérifier si un champ est sélectionné pour la collection courante
    const isFieldSelected = (fieldName) => {
        if (!selectedCollection || selectedCollection === "Sélectionner une collection") {
            return false;
        }
        const tagId = `${selectedCollection}-${fieldName}`;
        return selectedFieldTags.some(tag => tag.id === tagId);
    };



    // Fonctions pour gérer les tags ReactTags globaux
    const handleTagDelete = (tagIndex) => {
        const newTags = selectedFieldTags.filter((_, index) => index !== tagIndex);
        setSelectedFieldTags(newTags);
    };

    const handleTagAddition = (tag) => {
        // Format attendu: "collection - champ" ou juste "champ" (utilise la collection courante)
        let collection, field;
        
        if (tag.text.includes(' - ')) {
            [collection, field] = tag.text.split(' - ');
        } else {
            collection = selectedCollection;
            field = tag.text;
        }
        
        if (!collection || collection === "Sélectionner une collection") {
            toast.warning('Veuillez sélectionner une collection d\'abord');
            return;
        }
        
        // Vérifier que le champ existe dans la collection
        const cachedFields = allCollectionFields.get(collection);
        if (!cachedFields) {
            toast.warning(`Les champs de la collection ${collection} ne sont pas chargés`);
            return;
        }
        
        const fieldExists = cachedFields.some(f => f.name === field);
        if (!fieldExists) {
            toast.warning(`Le champ ${field} n'existe pas dans la collection ${collection}`);
            return;
        }
        
        const tagId = `${collection}-${field}`;
        const tagText = `${collection} - ${field}`;
        
        // Vérifier que le tag n'existe pas déjà
        if (selectedFieldTags.some(t => t.id === tagId)) {
            return;
        }
        
        const newTag = {
            id: tagId,
            text: tagText,
            collection: collection,
            field: field
        };
        
        setSelectedFieldTags([...selectedFieldTags, newTag]);
    };

    const handleTagDrag = (tag, currPos, newPos) => {
        const newTags = [...selectedFieldTags];
        newTags.splice(currPos, 1);
        newTags.splice(newPos, 0, tag);
        setSelectedFieldTags(newTags);
    };

    const onClearAllTags = () => {
        setSelectedFieldTags([]);
    };

    // Obtenir les suggestions de tags basées sur la collection courante
    const getTagSuggestions = () => {
        if (!selectedCollection || selectedCollection === "Sélectionner une collection") {
            return [];
        }
        
        return collectionFields
            .filter(field => !isFieldSelected(field.name))
            .map(field => ({ id: field.name, text: field.name }));
    };

    const handleExport = async (format) => {
        if (selectedFieldTags.length === 0) {
            toast.warning('Veuillez sélectionner au moins un champ à exporter');
            return;
        }

        setExporting(true);
        try {
            // Grouper les champs par collection
            const collectionGroups = {};
            selectedFieldTags.forEach(tag => {
                if (!collectionGroups[tag.collection]) {
                    collectionGroups[tag.collection] = [];
                }
                collectionGroups[tag.collection].push(tag.field);
            });

            // Préparer les données pour l'export multi-collections
            const exportData = Object.entries(collectionGroups).map(([collectionName, fields]) => ({
                collection: collectionName,
                fields: fields
            }));

            console.log('Export data:', exportData);
            await exportMultiCollectionData(exportData, format);
            toast.success(`Export ${format.toUpperCase()} généré avec succès !`);
        } catch (error) {
            console.error('Erreur lors de l\'export:', error);
            toast.error('Erreur lors de l\'export');
        } finally {
            setExporting(false);
        }
    };

    return (
        <>
            <div className={styles.shop}>
                <div className={styles.background}>
                    <img src="/images/content/bg-shop.jpg" alt="Background" />
                </div>

                <Card
                    className={cn(styles.card, className)}
                    title="Exporter"
                    classTitle="title-purple"
                >
                    <div className={styles.images}>
                        <Dropdown
                            className={styles.field}
                            label="Collections"
                            value={selectedCollection}
                            setValue={setSelectedCollection}
                            options={collections}
                            loading={loading}
                        />
                        
                        {selectedCollection && selectedCollection !== "Sélectionner une collection" && (
                            <>
                                <div className={styles.label}>
                                    Champs {loadingFields && "(Chargement...)"}
                                </div>
                                <div className={styles.list}>
                                    {collectionFields.map((field, index) => (
                                        <Checkbox
                                            className={styles.checkbox}
                                            content={`${field.name} (${field.type})`}
                                            value={isFieldSelected(field.name)}
                                            onChange={() => handleFieldChange(field.name)}
                                            key={index}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                        
                        <div className={styles.head}>
                            <div className={styles.label}>
                                Champs sélectionnés{" "}
                            </div>
                            <div className={styles.counter}>
                                <span>{selectedFieldTags.length}</span> champ{selectedFieldTags.length > 1 ? 's' : ''} sélectionné{selectedFieldTags.length > 1 ? 's' : ''}
                            </div>
                        </div>
                        
                        <div className={styles.tags}>
                            <ReactTags
                                handleDelete={handleTagDelete}
                                handleAddition={handleTagAddition}
                                handleDrag={handleTagDrag}
                                delimiters={delimiters}
                                suggestions={getTagSuggestions()}
                                placeholder="Tapez le nom d'un champ pour l'ajouter"
                                minQueryLength={1}
                                maxLength={50}
                                autofocus={false}
                                allowDeleteFromEmptyInput={true}
                                autocomplete={true}
                                readOnly={false}
                                allowUnique={true}
                                allowDragDrop={true}
                                inline={true}
                                inputFieldPosition="inline"
                                allowAdditionFromPaste={true}
                                editable={false}
                                clearAll={false}
                                tags={selectedFieldTags}
                            />
                            {selectedFieldTags.length > 0 && (
                                <button
                                    className={styles.clearButton}
                                    onClick={onClearAllTags}
                                    type="button"
                                >
                                    Tout supprimer
                                </button>
                            )}
                        </div>
                        
                        {selectedFieldTags.length > 0 && (
                            <div className={styles.exportButtons}>
                                <button
                                    className={cn("button", styles.button)}
                                    onClick={() => handleExport('excel')}
                                    disabled={exporting}
                                >
                                    {exporting ? "Traitement en cours..." : "Télécharger l'export"}
                                </button>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        </>
    );
};

export default Extract;
